import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import HomePage from "./HomePage";
import CapturePage from "./CapturePage";
import HistoryPage from "./HistoryPage";
import BottomNav from "./BottomNav";
import IzinListPage from "./Izinlistpage";
import IzinFormPage from "./IzinFormPage";
import LemburListPage from "./LemburListPage";
import LemburFormPage from "./LemburFormPage";
import Profile from "../Profile";
import { getCache } from "../../../../util/Cache";
import { LeaveRequest } from "../../../../controller/mobile/Izintypes";
import { getSeedLeaveRequests } from "../../../../controller/mobile/Izinmockapi";
import { getLemburList } from "../../../../controller/mobile/Lemburapi";
import type { LemburEntry } from "../../../../controller/mobile/Lemburtypes";
import { getSeedRecords, mockCurrentEmployee } from "../../../../controller/mobile/mockapi";
import type { AttendanceRecord, AttendanceType, Karyawan, Page } from "../../../../controller/mobile/types";
import { fetchPersonalData } from "../../../../controller/mobile/home";

export default function HomeMobilePersonalia(): React.ReactElement {
    const location = useLocation();
    const navigate = useNavigate();
    const routeSegment = location.pathname.replace(/^\/mobile\/personalia\/?/, "");
    const pagesByRoute: Record<string, Page> = {
        "": "home",
        capture: "capture",
        history: "history",
        izin: "izin",
        "izin/form": "izinlist",
        lembur: "lembur",
        "lembur/form": "lemburlist",
        profile: "profile",
        none: "none",
    };
    const page = pagesByRoute[routeSegment];
    const [captureType, setCaptureType] = useState<AttendanceType>("masuk");
    const [employee, setEmployee] = useState<Karyawan>(mockCurrentEmployee);
    const [records, setRecords] = useState<AttendanceRecord[]>([]);
    const [leave, setLeave] = useState<LeaveRequest[]>([]);
    const [lembur, setLembur] = useState<LemburEntry[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    useEffect(() => { initdata(); }, []);

    const setPage = (nextPage: Page) => {
        const routesByPage: Record<Page, string> = {
            home: "",
            capture: "capture",
            history: "history",
            izin: "izin",
            izinlist: "izin/form",
            lembur: "lembur",
            lemburlist: "lembur/form",
            profile: "profile",
            none: "none",
        };
        navigate(`/mobile/personalia${routesByPage[nextPage] ? `/${routesByPage[nextPage]}` : ""}`);
    };

    const initdata = async () => {
        setIsInitialLoading(true);
        try {
            const data: any = await getCache('current_user');
            if (!data?.id_karyawan) {
                console.warn('No current_user in cache');
                setIsInitialLoading(false);
                return;
            }

            const personalRes: any = await fetchPersonalData(data.id_karyawan);
            const dp = personalRes?.data?.[0];
            if (!dp) {
                console.warn('No personal data');
                setIsInitialLoading(false);
                return;
            }

            const dataPegawai = {
                id: dp.id,
                nama: dp.nama,
                level: 'user',
                jabatan: dp.jabatan,
                no_kary: dp.nik
            } as Karyawan;

            const [recordsResp, leaveResp, lemburResp] = await Promise.all([
                getSeedRecords(dataPegawai.id.toString()),
                getSeedLeaveRequests(dataPegawai.id.toString()),
                getLemburList(String(dataPegawai.id.toString()))
            ]);

            setEmployee(dataPegawai);
            setRecords((recordsResp && (recordsResp as any).data) || []);

            const leaveData = (leaveResp as any)?.data ? (leaveResp as any).data : (Array.isArray(leaveResp) ? leaveResp : []);
            const lemburData = (lemburResp as any)?.data ? (lemburResp as any).data : (Array.isArray(lemburResp) ? lemburResp : []);

            setLeave(leaveData);
            setLembur(lemburData);
        } catch (err) {
            console.error('Failed to load initial data:', err);
            setRecords([]); setLeave([]); setLembur([]);
        } finally {
            setIsInitialLoading(false);
        }
    };

    const handleStartCapture = (type: AttendanceType) => { setCaptureType(type); setPage("capture"); };
    const handleCaptureSuccess = async () => { await initdata(); setPage("home"); };

    if (!page) {
        return <Navigate to="/mobile/personalia" replace />;
    }

    return (
        <>
            {page === "home" && (
                <>
                    <div className="flex-1 overflow-hidden">
                        <HomePage
                            employee={employee}
                            records={records}
                            izinRecords={leave as any}
                            lemburRecords={lembur as any}
                            isLoading={isInitialLoading}
                            onNavigate={setPage}
                            onStartCapture={handleStartCapture}
                        />
                    </div>
                    <BottomNav active={page} onNavigate={setPage} onTap={initdata}/>
                </>
            )}
            {page === "history" && (
                <>
                    <div className="flex-1 overflow-hidden"><HistoryPage employee={employee} records={records} /></div>
                    <BottomNav active={page} onNavigate={setPage} onTap={initdata}/>
                </>
            )}
            {page === "capture" && (
                <CapturePage type={captureType} id_karyawan={employee.id.toString()} onCancel={() => setPage("home")} onSuccess={handleCaptureSuccess} />
            )}
            {page === "izin" && (
                <>
                    <IzinListPage requests={leave} onAddNew={() => setPage("izinlist")} />
                    <BottomNav active={page} onNavigate={setPage} onTap={initdata}/>
                </>
            )}
            {page === 'izinlist' && (
                <IzinFormPage idKaryawan={employee.id} onCancel={() => setPage("izin")} onSuccess={() => { setPage("izin"); initdata(); }} />
            )}
            {page === "lembur" && (
                <>
                    <LemburListPage entries={lembur} onAddNew={() => setPage("lemburlist")} />
                    <BottomNav active={page} onNavigate={setPage} onTap={initdata}/>
                </>
            )}
            {page === 'lemburlist' && (
                <LemburFormPage idKaryawan={employee.id} onCancel={() => setPage("lembur")} onSuccess={() => { setPage("lembur"); initdata(); }} />
            )}
            {page === 'profile' && (
                <>
                    <Profile onNavigate={setPage} />
                    <BottomNav active={page} onNavigate={setPage} onTap={initdata}/>
                </>
            )}
        </>
    )
}
